import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Menu, message, notification } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import {
	downloadWalkthroughCSV,
	downloadWalkthroughScoreCSV,
	generateWalkthroughPDF,
	getWalkthroughPDFStatus,
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { DownOutlined } from '@ant-design/icons';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { getBackLinkStates, goBackLink } from '../../utils/HistoryUtils';
import DeleteConfirmButton from '../delete_pop_confirm/DeletePopConfirm';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { WebSocketContext } from '../../context/WebSocketContext';
import {
	notify,
	shouldShowRealtimeNotePopup,
	WEB_SOCKET_MESSAGE_TYPES,
} from '../../utils/WebSocketUtils';
import { DEFAULT_NOTIFICATION_DURATION } from '../../utils/WorkOrderUtils';
import NewMessageNotification from '../work_orders_detail_page/NewMessageNotification';
import WalkthroughDetailListener from './WalkthroughDetailListener';
import { v4 as uuidv4 } from 'uuid';
import OWPoll from '../common/OWPoll';
import useMountEffect from '../../hooks/useMountEffect';
import {
	isLargePhotoPdfSupported,
	isWalkthroughDetailReady,
	PDF_EXPORT_MODES,
	pdfLinkFieldFor,
	walkthroughPhotoCount,
} from '../../utils/SiteSurveyUtils';
import WalkthroughPdfExportModal from './WalkthroughPdfExportModal';

require('./WalkthroughsDetailPage.less');

const WalkthroughsDetailPage: FC<any> = ({
	getWalkthrough,
	match,
	walkthroughsFetching,
	history,
	location,
	walkthrough,
	deleteWalkthrough,
	downloadWalkthroughCSV,
	downloadWalkthroughScoreCSV,
	fetchPdfStatus,
	generatePDF,
	childRoutes,
	companyConfig,
	userType,
}): React.ReactElement => {
	// Which variant is generating (null when none). Both variants share the one Export dropdown and the
	// one OWPoll slot, so this doubles as the poll's mode.
	const [generatingPdfMode, setGeneratingPdfMode] = useState(null);
	const [pdfModalVisible, setPdfModalVisible] = useState(false);
	// True from the moment a generate request is issued until it answers. generatingPdfMode cannot do
	// this job: setting it is what mounts OWPoll, and OWPoll would start polling before the backend's
	// detached future has created the export row — its first empty status reads as "finished".
	const [pdfRequestPending, setPdfRequestPending] = useState(false);
	const pdfLinkRef = useRef(null);
	const socketContext = useContext(WebSocketContext);

	const walkthroughId = useMemo(() => match.params.id, [match.params.id]);

	// Keep latest pathname in a ref so the listener (attached once) always reads current state
	const pathnameRef = useRef(location.pathname);
	useEffect(() => {
		pathnameRef.current = location.pathname;
	}, [location.pathname]);

	const handleMessage = useCallback(
		(event) => {
			try {
				const data = JSON.parse(event.data);

				if (pathnameRef.current.includes('notes')) return;
				if (data.walkthroughId != walkthroughId) return;

				switch (data.messageType) {
					case WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE:
						if (!shouldShowRealtimeNotePopup()) break;
						const notificationKey = uuidv4();
						notify();
						notification.open({
							key: notificationKey,
							message: <NewMessageNotification data={data} />,
							placement: 'topRight',
							duration: DEFAULT_NOTIFICATION_DURATION,
							onClick: () => {
								notification.close(notificationKey);
								history.replace(
									`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/notes`
								);
							},
						});
						break;
					default:
						break;
				}
			} catch (err) {
				console.log('JSON parse error', err);
			}
		},
		[walkthroughId, history, userType]
	);

	// Each variant caches its own link, so the short-circuit has to read the field for the variant that
	// was actually asked for — opening `pdfLink` for a larger-photo request would silently hand back the
	// standard PDF and never call the API.
	const downloadPDF = useCallback(
		(exportMode) => {
			setPdfModalVisible(false);
			const pdfLink = nullSafeGet(pdfLinkFieldFor(exportMode), walkthrough);
			if (pdfLink) {
				window.open(pdfLink, '_blank');
				return;
			}
			setPdfRequestPending(true);
			generatePDF(walkthroughId, { export_mode: exportMode })
				.then((res) => {
					message.success(nullSafeGet('data', res) || 'PDF generation initiated.');
					pdfLinkRef.current = null;
					setGeneratingPdfMode(exportMode);
				})
				.catch((err) => {
					message.error(err || 'Failed to generate PDF.');
				})
				.finally(() => setPdfRequestPending(false));
		},
		[generatePDF, walkthroughId, walkthrough]
	);

	const openPdfModal = useCallback(() => setPdfModalVisible(true), []);
	const closePdfModal = useCallback(() => setPdfModalVisible(false), []);

	const onPdfPollComplete = useCallback(
		(res) => {
			const completedResult = res && res.find((r) => r && r.pdfLink);
			if (completedResult) {
				pdfLinkRef.current = completedResult.pdfLink;
			}
			getWalkthrough(walkthroughId);
		},
		[getWalkthrough, walkthroughId]
	);

	const onOpenPdf = useCallback(() => {
		const pdfLink =
			pdfLinkRef.current || nullSafeGet(pdfLinkFieldFor(generatingPdfMode), walkthrough);
		if (pdfLink) {
			window.open(pdfLink, '_blank');
		}
		setGeneratingPdfMode(null);
	}, [walkthrough, generatingPdfMode]);

	// Scope the poll to the variant in flight; a mode-blind poll would report the other export's progress.
	const fetchPdfStatusFn = useMemo(
		() => fetchPdfStatus(walkthroughId, generatingPdfMode || PDF_EXPORT_MODES.STANDARD),
		[fetchPdfStatus, walkthroughId, generatingPdfMode]
	);

	useMountEffect(() => {
		getWalkthrough(walkthroughId).then((wt) => {
			// Pick up an export that was still running when the page was last left. A variant that
			// already has a cached link is finished and needs no check; the rest are tried in turn and
			// the first one still running takes the single progress slot.
			const resumeNext = (remainingModes) => {
				if (!remainingModes.length) return;
				const [exportMode, ...rest] = remainingModes;
				fetchPdfStatus(walkthroughId, exportMode)()
					.then((res) => {
						if (Array.isArray(res) && res.length > 0) {
							// Running: this variant takes the single progress slot, so stop here.
							setGeneratingPdfMode(exportMode);
							return;
						}
						if (res && res.completed && res.pdfLink) {
							// Finished since the page was last left. Record it, but keep scanning — the
							// other variant may still be running and would otherwise go untracked.
							pdfLinkRef.current = res.pdfLink;
							getWalkthrough(walkthroughId);
						}
						resumeNext(rest);
					})
					.catch(() => resumeNext(rest));
			};

			resumeNext(
				[PDF_EXPORT_MODES.STANDARD, PDF_EXPORT_MODES.LARGE_PHOTOS].filter(
					(exportMode) => !nullSafeGet(pdfLinkFieldFor(exportMode), wt)
				)
			);
		});
	});

	const handleTabChange = useCallback(
		(key) => history.push(`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/${key}`),
		[history, userType, walkthroughId]
	);

	const handleDelete = useCallback(() => {
		deleteWalkthrough(walkthrough).then(() => {
			history.push(
				goBackLink(
					location,
					`/${userType}/siteSurveys/detail/${nullSafeGet('siteSurveyId', walkthrough)}/details`
				)
			);
		});
	}, [deleteWalkthrough, history, location, userType, walkthrough]);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1;
		},
		[history.location.pathname]
	);

	const downloadCSV = useCallback(() => {
		downloadWalkthroughCSV(walkthroughId);
	}, [downloadWalkthroughCSV, walkthroughId]);

	const downloadScoreCSV = useCallback(() => {
		downloadWalkthroughScoreCSV(walkthroughId);
	}, [downloadWalkthroughScoreCSV, walkthroughId]);

	const tabs = useMemo(
		() => [
			{
				key: 'details',
				name: 'Overview',
			},
			{
				key: 'notes',
				name: 'Notes',
			},
		],
		[]
	);

	const largePhotosSupported = useMemo(() => isLargePhotoPdfSupported(walkthrough), [walkthrough]);
	const photoCount = useMemo(() => walkthroughPhotoCount(walkthrough), [walkthrough]);

	// One PDF entry; picking the photo size happens in the modal, which is also where the larger-photo
	// option can explain itself when the walkthrough has too many photos for it.
	const exportMenu = useMemo(
		() => (
			<Menu>
				<Menu.Item onClick={downloadScoreCSV}>Export Score CSV</Menu.Item>
				<Menu.Item onClick={downloadCSV}>Export CSV</Menu.Item>
				<Menu.Item onClick={openPdfModal}>Export PDF</Menu.Item>
			</Menu>
		),
		[downloadCSV, downloadScoreCSV, openPdfModal]
	);

	// The PDF options depend on the walkthrough's photo count, which only arrives with the detail — so
	// the menu must not be openable before then, or the larger-photo option would be offered on a count
	// nobody has yet.
	const exportReady = useMemo(
		() => isWalkthroughDetailReady(walkthrough, walkthroughId, walkthroughsFetching),
		[walkthroughsFetching, walkthrough, walkthroughId]
	);

	// Disabling while a generate request is in flight is what stops a second variant being submitted
	// before the first has answered — until it does, generatingPdfMode is still null and nothing else
	// would disable the control.
	const exportBusy = !exportReady || pdfRequestPending;

	// Shared by the idle button and the one OWPoll renders while an export runs, so the two cannot drift.
	// Deliberately not antd's `loading`: its spinner takes the icon slot and pushes the label out of the
	// ghost button's box. Keep the contents fixed and let `disabled` carry the busy state.
	const renderExportButton = useCallback(
		(disabled) => (
			<Dropdown overlay={exportMenu} disabled={disabled || exportBusy}>
				<Button style={{ marginRight: 16 }} ghost={true} disabled={disabled || exportBusy}>
					<div className="flex flex-row items-center">
						<span>Export</span>
						<div className="ml-2">
							<DownOutlined />
						</div>
					</div>
				</Button>
			</Dropdown>
		),
		[exportMenu, exportBusy]
	);

	const { backLinkText, backLinkTo } = useMemo(() => {
		const defaultBackLinkTo = `/${userType}/siteSurveys/detail/${nullSafeGet(
			'siteSurvey.id',
			walkthrough
		)}/details`;
		const states = getBackLinkStates(location, defaultBackLinkTo) as any;
		return {
			backLinkText: states.backLinkText || 'Back to site survey',
			backLinkTo: states.backLinkTo || defaultBackLinkTo,
		};
	}, [location, userType, walkthrough]);

	return (
		<div className="walkthroughsDetailPage">
			<WalkthroughDetailListener
				socketContext={socketContext}
				onMessage={handleMessage}
				entityIds={[walkthroughId]}
			/>
			<WalkthroughPdfExportModal
				visible={pdfModalVisible}
				largePhotosSupported={largePhotosSupported}
				photoCount={photoCount}
				onExport={downloadPDF}
				onCancel={closePdfModal}
			/>
			<DetailPageHeader
				headerClassName="walkthrough-detail-header"
				exactPath={`/${userType}/siteSurveys/walkthrough/detail/:id`}
				redirectPath={`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/details`}
				backLinkText={backLinkText}
				backLinkTo={backLinkTo}
				title={walkthroughsFetching ? null : walkthrough.title}
				subtitle={
					<div className="walkthroughsDetailPage__subtitleRow">
						<div className="walkthroughsDetailPage__subtitle">
							{nullSafeGet('location.name', walkthrough)}
						</div>
					</div>
				}
				actions={[
					generatingPdfMode ? (
						<OWPoll
							// Keyed by mode so each variant gets a fresh instance. With a constant key React
							// reuses the previous one, whose delay is already null from its own completion,
							// and OWPoll's restart effect only fires when successMessageMandatory *changes* —
							// so a second export started before the first is dismissed is never polled.
							key={`walkthrough-pdf-${generatingPdfMode}`}
							id="walkthrough-pdf"
							fetchStatusPromises={[fetchPdfStatusFn]}
							successMessage="PDF Generated Successfully!"
							successButtonText="Open PDF"
							message={
								generatingPdfMode === PDF_EXPORT_MODES.LARGE_PHOTOS
									? 'Exporting Walkthrough with larger photos'
									: 'Exporting Walkthrough'
							}
							onRefresh={onOpenPdf}
							onComplete={onPdfPollComplete}
							successMessageMandatory={!!generatingPdfMode}
							renderElementWithProps={(props) => renderExportButton(props.disabled)}
						/>
					) : (
						renderExportButton(false)
					),
					<AccessPermissionChecker name={PERMISSION_NAMES.DELETE_WALKTHROUGHS}>
						<DeleteConfirmButton
							onConfirm={handleDelete}
							title={'Are you sure you want to delete this walkthrough?'}
						>
							<Button key={2} ghost={true}>
								Delete
							</Button>
						</DeleteConfirmButton>
					</AccessPermissionChecker>,
				]}
				tabs={tabs}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				handleTabChange={handleTabChange}
				checkTabActive={checkTabActive}
				childRoutes={childRoutes}
			/>
		</div>
	);
};

const mapStateToProps = (state) => ({
	walkthroughsFetching: state.walkthroughs.fetching,
	walkthrough: state.walkthroughs.detail,
	companyConfig: state.company_config,
	currentUser: state.session.currentUser,
	loading: state.walkthroughs.fetching,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWalkthrough: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.readOne(id)
				: walkthroughsRestCrudThunksForBuyer.readOne(id)
		),
	deleteWalkthrough: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.delete(entity, 'id', null, true)
				: walkthroughsRestCrudThunksForBuyer.delete(entity, 'id', null, true)
		),
	downloadWalkthroughCSV: (id) => dispatch(downloadWalkthroughCSV(ownProps.userType)(id)),
	downloadWalkthroughScoreCSV: (id) => dispatch(downloadWalkthroughScoreCSV(ownProps.userType)(id)),
	generatePDF: (id, params) => dispatch(generateWalkthroughPDF(ownProps.userType)(id, params)),
	fetchPdfStatus: (id, exportMode) => () =>
		dispatch(getWalkthroughPDFStatus(ownProps.userType)(id, { export_mode: exportMode })),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(WalkthroughsDetailPage as any) as any)
);
